import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
    IonRefresher,
    IonRefresherContent,
    IonContent,
    IonLabel,
    IonSegment,
    IonSegmentButton,
    IonFooter,
} from "@ionic/react";
import LoadingSpinner from "../../components/LoadingSpinner";
import { AppState } from "../../store";
import {
    loadMyPermits,
    loadReferenceProcedures,
    loadSyncedUser,
    loadClientRoles,
    saveToken,
    tryToConnect,
} from "./thunks";
import HomeHeader from "../../components/HomeHeader";
import { UserProfile } from "../../models/UserProfile";
import { setShowExpiredPermitsFlag, setSelectedProject, closeSessionExpiryAlert } from "./actions";
import { setCurrentUser } from "../LoginContainer/actions";
import { Project } from "../../models/Project";
import { ReferenceProcedure } from "../../models/ReferenceProcedure";
import HomeFooter from "../../components/HomeFooter";
import { User } from "../../models/User";
import database from "../../Database";
import { Permit } from "../../models/Permit";
import database_Local from "../../Database_Local";
import { Role } from "../../models/Role";
import { partition, uniqBy } from "lodash";
import MyPermitsHeader from "../../components/MyPermitsHeader";
import MyPermitsList from "../../components/MyPermitsList";
import { useHistory } from "react-router";
import { ActionPerformed, PushNotificationSchema, PushNotifications, Token } from "@capacitor/push-notifications";
import SyncStatusProgressBar from "../../components/SyncStatusProgressBar";
import { ReplicatorStatus } from "../../models/ReplicatorStatus";
import { refreshReplicatorStatus, logout } from "../AppControlContainer/thunks";

interface MyPermitsContainerProps {
    permits: Permit[];
    clientRoles: Role[];
    referenceProcedures: ReferenceProcedure[];
    loading: boolean;
    showExpired: boolean;
    selectedProject: Project | null;
    loadMyPermits: typeof loadMyPermits;
    loadReferenceProcedures: typeof loadReferenceProcedures;
    loadClientRoles: typeof loadClientRoles;
    userProfile: UserProfile | null;
    currentUser: User | null;
    lastSessionExpiryAlertTime: Date | null;
    currentSyncStatus: ReplicatorStatus;
    setShowExpiredPermitsFlag: typeof setShowExpiredPermitsFlag;
    setSelectedProjectChanged: typeof setSelectedProject;
    logout: typeof logout;
    setCurrentUser: typeof setCurrentUser;
    loadSyncedUser: typeof loadSyncedUser;
    tryToConnect: typeof tryToConnect;
    saveToken: typeof saveToken;
    refreshReplicatorStatus: typeof refreshReplicatorStatus;
    closeSessionExpiryAlert: typeof closeSessionExpiryAlert;
}

const MyPermitsContainer: React.FC<MyPermitsContainerProps> = ({
    permits,
    clientRoles,
    referenceProcedures,
    loading,
    userProfile,
    currentUser,
    lastSessionExpiryAlertTime,
    currentSyncStatus,
    showExpired,
    setShowExpiredPermitsFlag,
    selectedProject,
    setSelectedProjectChanged,
    logout,
    loadMyPermits,
    loadReferenceProcedures,
    loadClientRoles,
    setCurrentUser,
    loadSyncedUser,
    tryToConnect,
    saveToken,
    refreshReplicatorStatus,
    closeSessionExpiryAlert,
}) => {
    let history = useHistory();
    const [selectedTab, setSelectedTab] = useState("assigned");
    const [permitsBatchTimeout, setPermitsBatchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [rolesBatchTimeout, setRolesBatchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [pushNotificationsRegistered, setPushNotificationsRegistered] = useState(false);

    useEffect(() => {
        refreshReplicatorStatus();
    }, [refreshReplicatorStatus]);

    // Function to handle individual document updates with batching
    const handleDocumentUpdate = (docId: string) => {
        if (docId.startsWith("permit::")) {
            // Clear existing permits timeout and set a new one
            if (permitsBatchTimeout) {
                clearTimeout(permitsBatchTimeout);
            }
            const newTimeout = setTimeout(() => {
                console.log("Loading my permits after batch timeout");
                loadMyPermits();
            }, 1000);
            setPermitsBatchTimeout(newTimeout);
        } else if (docId.startsWith("role::")) {
            // Clear existing roles timeout and set a new one
            if (rolesBatchTimeout) {
                clearTimeout(rolesBatchTimeout);
            }
            const newTimeout = setTimeout(() => {
                console.log("Loading client roles after batch timeout");
                if (currentUser) {
                    loadClientRoles(currentUser.client.id);
                }
            }, 1000);
            setRolesBatchTimeout(newTimeout);
        } else if (docId.startsWith("user::")) {
            // Load synced user immediately (no batching)
            const userId = docId.split("::")[1];
            if (userId) {
                console.log("Loading synced user immediately for ID:", userId);
                loadSyncedUser(userId);
            }
        }
    };

    useEffect(() => {
        if (currentUser) {
            if (clientRoles && clientRoles.length > 0) {
                loadMyPermits();
            }

            database.setReplicationCompletedEventHandler(handleDocumentUpdate);

            return () => {
                database.setReplicationCompletedEventHandler(undefined);
                if (permitsBatchTimeout) {
                    clearTimeout(permitsBatchTimeout);
                }
                if (rolesBatchTimeout) {
                    clearTimeout(rolesBatchTimeout);
                }
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, clientRoles, loadMyPermits]);

    useEffect(() => {
        if (currentUser) {
            loadReferenceProcedures(currentUser.client.id);
            loadClientRoles(currentUser?.client.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    // Handle push notification registration separately
    useEffect(() => {
        if (currentSyncStatus?.activityLevel?.toUpperCase() !== "IDLE") return;

        if (currentUser?.id && !pushNotificationsRegistered) {
            registerPushNotification();
            setPushNotificationsRegistered(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, currentSyncStatus, pushNotificationsRegistered]);

    const registerPushNotification = () => {
        // Request permission to use push notifications
        // iOS will prompt user and return if they granted permission or not
        // Android will just grant without prompting
        PushNotifications.requestPermissions().then((result) => {
            if (result.receive === "granted") {
                // Register with Apple / Google to receive push via APNS/FCM
                PushNotifications.register();
            } else {
                // Show some error
            }
        });

        //PushNotifications.removeAllListeners();
        // On success, we should be able to receive notifications
        PushNotifications.addListener("registration", (token: Token) => {
            console.log("Push registration successful");
            saveToken(token.value);
        });

        // Some issue with our setup and push will not work
        PushNotifications.addListener("registrationError", (error: any) => {
            console.log("Error on registration: " + JSON.stringify(error));
        });

        // Show us the notification payload if the app is open on our device
        PushNotifications.addListener("pushNotificationReceived", (notification: PushNotificationSchema) => {
            console.log("Push received: " + JSON.stringify(notification));
        });

        // Method called when tapping on a notification
        PushNotifications.addListener("pushNotificationActionPerformed", (notification: ActionPerformed) => {
            console.log("Push action performed: " + JSON.stringify(notification));
        });
    };

    const handleTryToConnect = () => {
        userProfile && tryToConnect(userProfile);
    };

    const doRefresh = () => {
        if (currentUser) {
            loadClientRoles(currentUser?.client.id);
            if (clientRoles && clientRoles.length > 0) {
                loadMyPermits();
            }
            loadReferenceProcedures(currentUser.client.id);
        }

        const refresher: any = document.getElementById("refresher");
        setTimeout(() => {
            refresher.complete();
        }, 1000);
    };

    const handleSelectClientUser = (user: User | undefined) => {
        if (user) {
            setCurrentUser(user);
            database_Local.save("currentUser", user);
        }
    };

    const handleLogOut = () => {
        logout();
    };

    const handleTabChange = (e: CustomEvent<any>) => {
        setSelectedTab(e.detail.value || "");
    };

    const userName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : "";
    const permitPartitions = partition(permits, (p) => {
        return p.permitAssignments?.find((a) => a.userId === currentUser?.id);
    });
    const permitsToDisplay = selectedTab === "assigned" ? permitPartitions[0] : permitPartitions[1];
    const selectedProjectPermits =
        selectedProject === null ? [] : permitsToDisplay.filter((p) => p.project.id === selectedProject.id);

    const handleSelectPermit = (id: string): void => {
        history.push(`/home/permit/${id}`);
    };

    const projects = uniqBy(
        permits.map((permit) => permit.project),
        (project) => project.id
    );

    const handleCloseSessionExpiryAlert = (): void => {
        closeSessionExpiryAlert(new Date());
    };

    return (
        <>
            <IonContent>
                <SyncStatusProgressBar
                    currentSyncStatus={currentSyncStatus}
                    lastSessionExpiryAlertTime={lastSessionExpiryAlertTime}
                    onReLogin={handleLogOut}
                    onTryToConnect={handleTryToConnect}
                    onCloseSessionExpiryAlert={handleCloseSessionExpiryAlert}
                />
                <HomeHeader
                    currentUser={currentUser}
                    clientUsers={userProfile?.clientUsers}
                    onSelectClientUser={handleSelectClientUser}
                    onLogOut={handleLogOut}
                    userName={userName}
                />
                <IonRefresher slot="fixed" onIonRefresh={doRefresh} id="refresher">
                    <IonRefresherContent></IonRefresherContent>
                </IonRefresher>
                <MyPermitsHeader
                    showExpired={showExpired}
                    onShowExpiredChange={setShowExpiredPermitsFlag}
                    selectedProject={selectedProject}
                    projects={projects}
                    onSelectedProjectChange={setSelectedProjectChanged}
                />
                <MyPermitsList
                    onSelectPermit={handleSelectPermit}
                    permits={selectedProjectPermits}
                    showExpired={showExpired}
                />
                {loading && <LoadingSpinner />}
            </IonContent>
            <IonFooter>
                <IonSegment color="dark" value={selectedTab} onIonChange={handleTabChange}>
                    <IonSegmentButton value="assigned">
                        <IonLabel>Assigned to me</IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="all">
                        <IonLabel>Other permits</IonLabel>
                    </IonSegmentButton>
                </IonSegment>
                <HomeFooter
                    referenceProcedures={referenceProcedures.filter(
                        (r) => r.projectId === null || (selectedProject && r.projectId === selectedProject.id)
                    )}
                />
            </IonFooter>
        </>
    );
};

function mapStateToProps(state: AppState) {
    return {
        loading: state.myPermits.loading,
        showExpired: state.myPermits.showExpired,
        permits: state.myPermits.permits,
        clientRoles: state.myPermits.clientRoles,
        referenceProcedures: state.myPermits.referenceProcedures,
        userProfile: state.user.currentProfile,
        currentUser: state.user.currentUser,
        selectedProject: state.myPermits.selectedProject,
        currentSyncStatus: state.appControl.currentSyncStatus,
        lastSessionExpiryAlertTime: state.myPermits.lastSessionExpiryAlertTime,
    };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        loadMyPermits: () => dispatch(loadMyPermits()),
        loadReferenceProcedures: (clientId: string) => dispatch(loadReferenceProcedures(clientId)),
        loadClientRoles: (clientId: string) => dispatch(loadClientRoles(clientId)),
        setShowExpiredPermitsFlag: (value: boolean) => dispatch(setShowExpiredPermitsFlag(value)),
        setSelectedProjectChanged: (value: Project | null) => dispatch(setSelectedProject(value)),
        logout: () => dispatch(logout()),
        setCurrentUser: (user: User) => dispatch(setCurrentUser(user)),
        loadSyncedUser: (userId: string) => dispatch(loadSyncedUser(userId)),
        tryToConnect: (userProfile: UserProfile) => dispatch(tryToConnect(userProfile)),
        saveToken: (token: string) => dispatch(saveToken(token)),
        closeSessionExpiryAlert: (value: Date) => dispatch(closeSessionExpiryAlert(value)),
        refreshReplicatorStatus: () => dispatch(refreshReplicatorStatus()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MyPermitsContainer);
